import { useEffect, useState } from 'react';

/**
 * Subscribes to a CSS media query.
 *
 * Uses `useState` initializer + `addEventListener` so it stays correct across
 * Teams window resizes and device rotation.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent): void => setMatches(event.matches);

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
}

/** Tailwind `md` breakpoint. Below this the shell switches to the mobile nav. */
export const MOBILE_QUERY = '(max-width: 767px)';
/** Tailwind `lg` breakpoint, used to auto-collapse the side navigation. */
export const COMPACT_QUERY = '(max-width: 1279px)';

export const useIsMobile = (): boolean => useMediaQuery(MOBILE_QUERY);
export const useIsCompact = (): boolean => useMediaQuery(COMPACT_QUERY);
export const usePrefersReducedMotion = (): boolean =>
  useMediaQuery('(prefers-reduced-motion: reduce)');
